import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './utils/ThemeProvider';
import { LanguageProvider } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { Layout } from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { AdminRoute } from './components/AdminRoute';
import { AdminLayout } from './components/AdminLayout';

// Keep login pages as regular imports for fastest initial load
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AdminLogin } from './pages/AdminLogin';

// Helper function to handle both default and named exports for lazy loading
const lazyLoad = (importFn, exportName = null) => {
    return lazy(() => {
        return importFn().then(module => {
            // If exportName is provided, use named export, otherwise use default
            const component = exportName ? module[exportName] : (module.default || module);
            if (!component) {
                throw new Error(`Component ${exportName || 'default'} not found in module`);
            }
            return { default: component };
        });
    });
};

// Lazy load all other pages for code splitting
// Named exports
const Home = lazyLoad(() => import('./pages/Home'), 'Home');
const AdminDashboard = lazyLoad(() => import('./pages/admin/AdminDashboard'), 'AdminDashboard');
const AdminAnnouncements = lazyLoad(() => import('./pages/admin/AdminAnnouncements'), 'AdminAnnouncements');
const AdminAnnouncementView = lazyLoad(() => import('./pages/admin/AdminAnnouncementView'), 'AdminAnnouncementView');
const AdminAnnouncementForm = lazyLoad(() => import('./pages/admin/AdminAnnouncementForm'), 'AdminAnnouncementForm');
const AdminNewsletters = lazyLoad(() => import('./pages/admin/AdminNewsletters'), 'AdminNewsletters');
const AdminNewsletterForm = lazyLoad(() => import('./pages/admin/AdminNewsletterForm'), 'AdminNewsletterForm');
const AdminVideos = lazyLoad(() => import('./pages/admin/AdminVideos'), 'AdminVideos');
const AdminVideoForm = lazyLoad(() => import('./pages/admin/AdminVideoForm'), 'AdminVideoForm');
const AdminVideoManage = lazyLoad(() => import('./pages/admin/AdminVideoManage'), 'AdminVideoManage');
const AdminMeetings = lazyLoad(() => import('./pages/admin/AdminMeetings'), 'AdminMeetings');
const AdminMeetingForm = lazyLoad(() => import('./pages/admin/AdminMeetingForm'), 'AdminMeetingForm');
const AdminMeetingManage = lazyLoad(() => import('./pages/admin/AdminMeetingManage'), 'AdminMeetingManage');
const AdminSlides = lazyLoad(() => import('./pages/admin/AdminSlides'), 'AdminSlides');
const AdminKingspassEvents = lazyLoad(() => import('./pages/admin/AdminKingspassEvents'), 'AdminKingspassEvents');
const AdminKingspassEventForm = lazyLoad(() => import('./pages/admin/AdminKingspassEventForm'), 'AdminKingspassEventForm');
const AdminKingspassEventView = lazyLoad(() => import('./pages/admin/AdminKingspassEventView'), 'AdminKingspassEventView');
const AdminKingspassEventAttendees = lazyLoad(() => import('./pages/admin/AdminKingspassEventAttendees'), 'AdminKingspassEventAttendees');
const AdminKingspassEventDepartments = lazyLoad(() => import('./pages/admin/AdminKingspassEventDepartments'), 'AdminKingspassEventDepartments');
const AdminKingspassEventDepartmentStaff = lazyLoad(() => import('./pages/admin/AdminKingspassEventDepartmentStaff'), 'AdminKingspassEventDepartmentStaff');
const AdminKingspassEventRegistered = lazyLoad(() => import('./pages/admin/AdminKingspassEventRegistered'), 'AdminKingspassEventRegistered');
const AdminKingspassEventRegions = lazyLoad(() => import('./pages/admin/AdminKingspassEventRegions'), 'AdminKingspassEventRegions');
const AdminKingspassEventRegionStaff = lazyLoad(() => import('./pages/admin/AdminKingspassEventRegionStaff'), 'AdminKingspassEventRegionStaff');
const AdminStaffEvents = lazyLoad(() => import('./pages/admin/AdminStaffEvents'), 'AdminStaffEvents');
const AdminStaffEventForm = lazyLoad(() => import('./pages/admin/AdminStaffEventForm'), 'AdminStaffEventForm');
const AdminStaffEventView = lazyLoad(() => import('./pages/admin/AdminStaffEventView'), 'AdminStaffEventView');
const AdminRegions = lazyLoad(() => import('./pages/admin/AdminRegions'), 'AdminRegions');
const AdminRegionForm = lazyLoad(() => import('./pages/admin/AdminRegionForm'), 'AdminRegionForm');
const AdminRegionView = lazyLoad(() => import('./pages/admin/AdminRegionView'), 'AdminRegionView');
const AdminDepartments = lazyLoad(() => import('./pages/admin/AdminDepartments'), 'AdminDepartments');
const AdminDepartmentForm = lazyLoad(() => import('./pages/admin/AdminDepartmentForm'), 'AdminDepartmentForm');
const AdminDepartmentView = lazyLoad(() => import('./pages/admin/AdminDepartmentView'), 'AdminDepartmentView');
const AdminStaffProfile = lazyLoad(() => import('./pages/admin/AdminStaffProfile'), 'AdminStaffProfile');
const AdminStaffList = lazyLoad(() => import('./pages/admin/AdminStaffList'), 'AdminStaffList');
const AdminCalendarEvents = lazyLoad(() => import('./pages/admin/AdminCalendarEvents'), 'AdminCalendarEvents');
const AdminStaffNew = lazyLoad(() => import('./pages/admin/AdminStaffNew'), 'AdminStaffNew');
const AdminStaffBirthdays = lazyLoad(() => import('./pages/admin/AdminStaffBirthdays'), 'AdminStaffBirthdays');
const AdminAdmins = lazyLoad(() => import('./pages/admin/AdminAdmins'), 'AdminAdmins');
const AdminRanks = lazyLoad(() => import('./pages/admin/AdminRanks'), 'AdminRanks');
const AdminMarketplace = lazyLoad(() => import('./pages/admin/AdminMarketplace'), 'AdminMarketplace');
const AdminMarketplaceForm = lazyLoad(() => import('./pages/admin/AdminMarketplaceForm'), 'AdminMarketplaceForm');
const AdminStaffCounselling = lazyLoad(() => import('./pages/admin/AdminStaffCounselling'), 'AdminStaffCounselling');
const AdminStaffWeekAttire = lazyLoad(() => import('./pages/admin/AdminStaffWeekAttire'), 'AdminStaffWeekAttire');
const AdminAppVersion = lazyLoad(() => import('./pages/admin/AdminAppVersion'), 'AdminAppVersion');
const AdminAnalytics = lazyLoad(() => import('./pages/admin/AdminAnalytics'), 'AdminAnalytics');
const NotFound = lazyLoad(() => import('./pages/NotFound'), 'NotFound');

// Default exports
const Profile = lazy(() => import('./pages/Profile'));
const InformationCenter = lazy(() => import('./pages/InformationCenter'));
const InformationCenterShow = lazy(() => import('./pages/InformationCenterShow'));
const NewsletterShow = lazy(() => import('./pages/NewsletterShow'));
const Videos = lazy(() => import('./pages/Videos'));
const VideoShow = lazy(() => import('./pages/VideoShow'));
const Events = lazy(() => import('./pages/Events'));
const StaffEventView = lazy(() => import('./pages/StaffEventView'));
const KingsPassEventView = lazy(() => import('./pages/KingsPassEventView'));
const Meetings = lazy(() => import('./pages/Meetings'));
const MeetingAttend = lazy(() => import('./pages/MeetingAttend'));
const MeetingView = lazy(() => import('./pages/MeetingView'));
const AcceptMeetings = lazy(() => import('./pages/AcceptMeetings'));
const NDAForm = lazy(() => import('./pages/NDAForm'));
const StaffWeekAttire = lazy(() => import('./pages/StaffWeekAttire'));
const StaffAttirePickup = lazy(() => import('./pages/StaffAttirePickup'));
const StaffAttireScan = lazy(() => import('./pages/StaffAttireScan'));
const STDL = lazy(() => import('./pages/STDL'));
const StaffHandbook = lazy(() => import('./pages/StaffHandbook'));
const StaffCounselling = lazy(() => import('./pages/StaffCounselling'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const Chat = lazy(() => import('./pages/Chat'));
const EdenAI = lazy(() => import('./pages/EdenAI'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Birthday = lazy(() => import('./pages/Birthday'));
const Notifications = lazy(() => import('./pages/Notifications'));

// Loading component for Suspense fallback
const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
    </div>
);

function PrivateRoute({ children, adminOnly = false }) {
    const { isAuthenticated, loading, user, isImpersonating } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    // Store intended redirect before redirecting to login
    if (!isAuthenticated) {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/login' && currentPath !== '/pilot/login') {
        localStorage.setItem('intended_redirect', currentPath);
      }
      // Redirect to appropriate login based on route
      if (adminOnly || currentPath.startsWith('/pilot')) {
        return <Navigate to="/pilot/login" replace />;
      }
      return <Navigate to="/login" replace />;
    }
    
    // If route requires admin but user is staff, redirect to staff home
    if (adminOnly && user && user.type !== 'admin') {
      return <Navigate to="/" replace />;
    }
    
    // If route is staff route but user is admin, redirect to admin dashboard
    if (!adminOnly && user && user.type === 'admin' && !window.location.pathname.startsWith('/pilot')) {
      return <Navigate to="/pilot/dashboard" replace />;
    }

    // Staff must verify KingsChat before proceeding to any other page
    // Skip when an admin is impersonating the staff account.
    if (!adminOnly && user?.type === 'staff' && user?.requires_kc_verification && !isImpersonating) {
      const path = window.location.pathname;
      if (path !== '/profile') {
        return <Navigate to="/profile?verify_required=1" replace />;
      }
    }

    return children;
}

function PublicRoute({ children, allowAuthenticated = false }) {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    // Allow authenticated users if allowAuthenticated is true (admin login page handles its own redirect to /pilot/dashboard)
    if (allowAuthenticated) {
        return children;
    }
    
    // Staff login: if already authenticated, go to staff dashboard (home)
    return !isAuthenticated ? children : <Navigate to="/" replace />;
}

function AppRoutes() {
    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route path="/login" element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                } />
                <Route path="/login/forgot-password" element={
                    <PublicRoute>
                        <ForgotPassword />
                    </PublicRoute>
                } />
                <Route path="/login/reset-password" element={
                    <PublicRoute>
                        <ResetPassword />
                    </PublicRoute>
                } />
                <Route path="/" element={
                    <PrivateRoute adminOnly={false}>
                        <Layout />
                    </PrivateRoute>
                }>
                    <Route index element={<Home />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="chat" element={<Chat />} />
                    <Route path="eden-ai" element={<EdenAI />} />
                    <Route path="calendar" element={<Calendar />} />
                    <Route path="information-center" element={<InformationCenter />} />
                    <Route path="information-center/:id" element={<InformationCenterShow />} />
                    <Route path="information-center/newsletters/:id" element={<NewsletterShow />} />
                    <Route path="videos" element={<Videos />} />
                    <Route path="videos/:id/:slug" element={<VideoShow />} />
                    <Route path="staff-events" element={<Events />} />
                    <Route path="staff-events/:id/:slug" element={<StaffEventView />} />
                    <Route path="events" element={<Events />} />
                    <Route path="events/:id" element={<KingsPassEventView />} />
                    <Route path="meetings" element={<Meetings />} />
                    <Route path="meetings/:id/view" element={<MeetingView />} />
                    <Route path="meetings/:code" element={<MeetingAttend />} />
                    <Route path="live-meetings" element={<Meetings />} />
                    <Route path="accept-meetings" element={<AcceptMeetings />} />
                    <Route path="nda-form" element={<NDAForm />} />
                    <Route path="stdl" element={<STDL />} />
                    <Route path="staff-handbook" element={<StaffHandbook />} />
                    <Route path="staff-counselling" element={<StaffCounselling />} />
                    <Route path="staffweek-attire" element={<StaffWeekAttire />} />
                    <Route path="staffweek-attire/pickup/:portalId" element={<StaffAttirePickup />} />
                    <Route path="staffweek-attire/scan" element={<StaffAttireScan />} />
                    <Route path="contact-us" element={<ContactUs />} />
                    <Route path="birthday/:id" element={<Birthday />} />
                    <Route path="notifications" element={<Notifications />} />
                </Route>
                {/* Admin Routes - login must be before nested routes */}
                <Route path="/pilot/login" element={
                    <PublicRoute allowAuthenticated={true}>
                        <AdminLogin />
                    </PublicRoute>
                } />
                <Route path="/pilot" element={
                    <PrivateRoute adminOnly={true}>
                        <AdminRoute>
                            <AdminLayout />
                        </AdminRoute>
                    </PrivateRoute>
                }>
                    <Route index element={<AdminDashboard />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="announcements" element={<AdminAnnouncements />} />
                    <Route path="announcements/create" element={<AdminAnnouncementForm />} />
                    <Route path="announcements/:id/edit" element={<AdminAnnouncementForm />} />
                    <Route path="announcements/:id" element={<AdminAnnouncementView />} />
                    <Route path="newsletters" element={<AdminNewsletters />} />
                    <Route path="newsletters/create" element={<AdminNewsletterForm />} />
                    <Route path="newsletters/:id/edit" element={<AdminNewsletterForm />} />
                    <Route path="videos" element={<AdminVideos />} />
                    <Route path="videos/create" element={<AdminVideoForm />} />
                    <Route path="videos/:id/edit" element={<AdminVideoForm />} />
                    <Route path="videos/:id/manage" element={<AdminVideoManage />} />
                    <Route path="meetings" element={<AdminMeetings />} />
                    <Route path="meetings/create" element={<AdminMeetingForm />} />
                    <Route path="meetings/:id/edit" element={<AdminMeetingForm />} />
                    <Route path="meetings/:id/manage" element={<AdminMeetingManage />} />
                    <Route path="kingspass-events" element={<AdminKingspassEvents />} />
                    <Route path="kingspass-events/create" element={<AdminKingspassEventForm />} />
                    <Route path="kingspass-events/:id/edit" element={<AdminKingspassEventForm />} />
                    <Route path="kingspass-events/:id/attendees/registered" element={<AdminKingspassEventRegistered />} />
                    <Route path="kingspass-events/:id/attendees/departments/:departmentId/staff" element={<AdminKingspassEventDepartmentStaff />} />
                    <Route path="kingspass-events/:id/attendees/departments" element={<AdminKingspassEventDepartments />} />
                    <Route path="kingspass-events/:id/attendees/regions" element={<AdminKingspassEventRegions />} />
                    <Route path="kingspass-events/:id/attendees/regions/:regionId/staff" element={<AdminKingspassEventRegionStaff />} />
                    <Route path="kingspass-events/:id/attendees" element={<AdminKingspassEventAttendees />} />
                    <Route path="kingspass-events/:id/edit" element={<AdminKingspassEventForm />} />
                    <Route path="kingspass-events/:id" element={<AdminKingspassEventView />} />
                    <Route path="staff-events" element={<AdminStaffEvents />} />
                    <Route path="staff-events/create" element={<AdminStaffEventForm />} />
                    <Route path="staff-events/:id/edit" element={<AdminStaffEventForm />} />
                    <Route path="staff-events/:id" element={<AdminStaffEventView />} />
                    <Route path="calendar-events" element={<AdminCalendarEvents />} />
                    <Route path="slides" element={<AdminSlides />} />
                    <Route path="regions" element={<AdminRegions />} />
                    <Route path="regions/create" element={<AdminRegionForm />} />
                    <Route path="regions/:id" element={<AdminRegionView />} />
                    <Route path="regions/:id/edit" element={<AdminRegionForm />} />
                    <Route path="departments" element={<AdminDepartments />} />
                    <Route path="departments/create" element={<AdminDepartmentForm />} />
                    <Route path="departments/:id" element={<AdminDepartmentView />} />
                    <Route path="departments/:id/edit" element={<AdminDepartmentForm />} />
                    <Route path="staff/new" element={<AdminStaffNew />} />
                    <Route path="staff/birthdays" element={<AdminStaffBirthdays />} />
                    <Route path="staff" element={<AdminStaffList filter="all" />} />
                    <Route path="staff/active" element={<AdminStaffList filter="active" />} />
                    <Route path="staff/disengaged" element={<AdminStaffList filter="disengaged" />} />
                    <Route path="staff/hq" element={<AdminStaffList filter="hq" />} />
                    <Route path="staff/lagos" element={<AdminStaffList filter="lagos" />} />
                    <Route path="staff/:id" element={<AdminStaffProfile />} />
                    <Route path="admins" element={<AdminAdmins kind="primary" />} />
                    <Route path="admins/directors" element={<AdminAdmins kind="directors" />} />
                    <Route path="admins/sdms" element={<AdminAdmins kind="sdms" />} />
                    <Route path="admins/payroll" element={<AdminAdmins kind="payroll" />} />
                    <Route path="admins/pastors" element={<AdminAdmins kind="pastors" />} />
                    <Route path="ranks" element={<AdminRanks />} />
                    <Route path="marketplace" element={<AdminMarketplace />} />
                    <Route path="marketplace/create" element={<AdminMarketplaceForm />} />
                    <Route path="marketplace/:id/edit" element={<AdminMarketplaceForm />} />
                    <Route path="staff-counselling" element={<AdminStaffCounselling />} />
                    <Route path="staffweek-attire" element={<AdminStaffWeekAttire />} />
                    <Route path="app-version" element={<AdminAppVersion />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                </Route>
                {/* Catch-all route for 404 */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Suspense>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <ThemeProvider>
                    <LanguageProvider>
                        <NotificationProvider>
                            <AuthProvider>
                                <AppRoutes />
                            </AuthProvider>
                        </NotificationProvider>
                    </LanguageProvider>
                </ThemeProvider>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
